import React, { useState, useContext } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHeadset,
    faPaperPlane,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

const AssignTicketToAgentList = ({
    show,
    onHide,
    ticket,
    currentUserId,
    currentUserName,
    onConfirm,
    inline = false
}) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!ticket) return null;

    const ticketId = ticket.idTicket || ticket.id;

    const handleConfirmAssignment = async () => {
        if (!currentUserId) return;

        setIsSubmitting(true);
        try {
            await onConfirm(ticket, {
                idUsuario: Number(currentUserId),
                vNombreCompleto: currentUserName || 'Asesor'
            });
            if (onHide) onHide();
        } finally {
            setIsSubmitting(false);
        }
    };

    if (inline) {
        return (
            <div className="d-flex flex-column gap-3 mb-3">
                {/* Tarjeta Principal: Tomar ticket y comenzar atención */}
                <div
                    className="p-3 rounded-3 border d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 shadow-none"
                    style={{
                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.08)' : '#eff6ff',
                        borderColor: isDark ? 'rgba(59, 130, 246, 0.25)' : '#bfdbfe'
                    }}
                >
                    <div className="d-flex align-items-start gap-3">
                        <div
                            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                                width: '46px',
                                height: '46px',
                                backgroundColor: '#2563eb',
                                color: '#ffffff',
                                fontSize: '1.25rem'
                            }}
                        >
                            <FontAwesomeIcon icon={faHeadset} />
                        </div>
                        <div>
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                <h6 className="fw-bold mb-0" style={{ fontSize: '1.02rem', color: isDark ? '#f8fafc' : '#1e3a8a' }}>
                                    Tomar ticket y comenzar atención
                                </h6>
                                <span
                                    className="badge px-2 py-0.5"
                                    style={{
                                        fontSize: '0.68rem',
                                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                                        color: isDark ? '#93c5fd' : '#1d4ed8',
                                        border: isDark ? '1px solid rgba(147, 197, 253, 0.3)' : '1px solid #bfdbfe'
                                    }}
                                >
                                    Requerido
                                </span>
                            </div>
                            <p className="mb-2 text-muted" style={{ fontSize: '0.90rem', maxWidth: '620px', lineHeight: 2 }}>
                                Este caso se encuentra pendiente de un asesor. Al tomar el ticket, te asignarás inmediatamente como responsable,
                                se notificará al cliente y se iniciará la medición de tu primer tiempo de respuesta.
                            </p>
                        </div>
                    </div>

                    {/* Botón de acción derecha */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-md-auto">
                        <Button
                            variant="primary"
                            size="sm"
                            className="px-3 py-2 fw-semibold d-flex align-items-center gap-2 shadow-sm"
                            disabled={isSubmitting}
                            onClick={handleConfirmAssignment}
                            style={{ fontSize: '0.86rem', borderRadius: '8px' }}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    <span>Asignando...</span>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faUserPlus} />
                                    <span>Asignarme este Ticket</span>
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            contentClassName={`border-0 shadow-lg overflow-hidden ${isDark ? 'dark__bg-1000 text-light' : 'bg-white text-dark'}`}
        >
            <Modal.Header
                closeButton
                closeVariant={isDark ? 'white' : undefined}
                className="py-3 px-3 d-flex align-items-center justify-content-between"
                style={{
                    backgroundColor: isDark ? '#16222f' : '#f8fafc',
                    borderBottom: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
                }}
            >
                <div className="d-flex align-items-center gap-2">
                    <div
                        className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                            width: '36px',
                            height: '36px',
                            backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
                            color: isDark ? '#38bdf8' : '#0284c7'
                        }}
                    >
                        <FontAwesomeIcon icon={faHeadset} style={{ fontSize: '1rem' }} />
                    </div>
                    <Modal.Title
                        className="modal-title mb-0 fw-bold"
                        style={{ color: isDark ? '#f8fafc' : '#0f172a', fontSize: '1.15rem' }}
                    >
                        Asignar Ticket #{ticketId}
                    </Modal.Title>
                </div>
            </Modal.Header>

            <Modal.Body
                className="py-3 px-3"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    color: isDark ? '#e2e8f0' : '#1e293b'
                }}
            >
                {/* Info del Ticket */}
                <div
                    className="p-3 rounded-2 mb-3"
                    style={{
                        backgroundColor: isDark ? '#1a2736' : '#f8fafc',
                        border: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`,
                        fontSize: '0.85rem'
                    }}
                >
                    <p className="mb-1" style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                        <strong style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>Cliente:</strong> {ticket.vCustomerName?.trim() || ticket.customerName?.trim() || ticket.vNombreContacto?.trim() || (ticket.vUsername ? `@${ticket.vUsername}` : null) || 'Cliente'}
                    </p>
                    <p className="mb-0" style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                        <strong style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>Teléfono:</strong> {ticket.vTelefono || ticket.phone || 'Sin teléfono'}
                    </p>
                </div>

                {/* Mensaje de Confirmación / Explicación del flujo */}
                <div
                    className="rounded-2 p-3 mb-0 d-flex align-items-start gap-2"
                    style={{
                        backgroundColor: isDark ? 'rgba(56, 189, 248, 0.12)' : '#e0f2fe',
                        border: `1px solid ${isDark ? 'rgba(56, 189, 248, 0.25)' : '#bae6fd'}`,
                        color: isDark ? '#7dd3fc' : '#0369a1',
                        fontSize: '0.82rem'
                    }}
                >
                    <FontAwesomeIcon icon={faPaperPlane} className="mt-1 flex-shrink-0" style={{ color: isDark ? '#38bdf8' : '#0284c7' }} />
                    <div>
                        Al confirmar, el ticket pasará a <strong style={{ color: isDark ? '#bae6fd' : '#0c4a6e' }}>En Progreso</strong> y te asignarás como asesor responsable. Se enviará automáticamente el mensaje de WhatsApp al cliente con tu saludo personalizado.
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer
                className="d-flex justify-content-end gap-2 py-2 px-3"
                style={{
                    backgroundColor: isDark ? '#16222f' : '#f8fafc',
                    borderTop: `1px solid ${isDark ? '#233142' : '#e2e8f0'}`
                }}
            >
                <Button
                    variant="danger"
                    size="sm"
                    onClick={onHide}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 fw-semibold shadow-none"
                    style={{ borderRadius: '6px' }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleConfirmAssignment}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 d-flex align-items-center gap-2 fw-semibold shadow-sm"
                    style={{ borderRadius: '6px' }}
                >
                    {isSubmitting ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span>Asignando...</span>
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faUserPlus} />
                            <span>Asignarme a mí</span>
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignTicketToAgentList;
